import { OperationError } from '../observability/errors';
import { CATEGORY, CODE, OPERATION, OPERATIONS, requireMember, validHttpStatus } from '../observability/contract';

const REACT_APP_GCASH_DEBUG_MODE = process.env.REACT_APP_GCASH_DEBUG_MODE;
const REACT_APP_JWT_TOKEN = process.env.REACT_APP_JWT_TOKEN;
const METHODS = new Set(['GET', 'POST', 'PUT', 'DELETE']);

async function request(url, params = {}, newHeaders = {}, method = 'GET', operation = OPERATION.API_OTHER) {
    requireMember(operation, OPERATIONS, 'operation');
    if (!METHODS.has(method) || typeof url !== 'string' || url.trim() === '') {
        throw new TypeError('Invalid Fetch request');
    }
    if (!params || typeof params !== 'object' || Array.isArray(params) ||
        !newHeaders || typeof newHeaders !== 'object' || Array.isArray(newHeaders)) {
        throw new TypeError('Fetch parameters and headers must be objects');
    }
    const target = new URL(url, window.location.origin);
    const options = {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(REACT_APP_GCASH_DEBUG_MODE && {
                'ngrok-skip-browser-warning': true,
                'x-authorization': REACT_APP_JWT_TOKEN,
            }),
            ...newHeaders,
        },
    };
    if (method === 'GET') {
        for (const [key, value] of Object.entries(params)) {
            if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
                throw new TypeError('Fetch query parameters must be scalar');
            }
            target.searchParams.set(key, String(value));
        }
    } else {
        options.body = JSON.stringify(params);
    }

    let response;
    try {
        response = await fetch(target.toString(), options);
    } catch (cause) {
        throw new OperationError({ operation, category: CATEGORY.NETWORK, code: CODE.NETWORK_ERROR, cause });
    }
    const status = validHttpStatus(response?.status);
    if (status === undefined) {
        throw new OperationError({ operation, category: CATEGORY.INVALID_RESPONSE, code: CODE.INVALID_RESPONSE });
    }
    const requestId = response.headers?.get?.('X-Request-ID');
    if (!response.ok) {
        throw new OperationError({ operation, category: CATEGORY.HTTP, code: CODE.UNKNOWN_API_ERROR, status, requestId });
    }
    if (status === 204 || status === 205) return null;
    try {
        return await response.json();
    } catch (cause) {
        throw new OperationError({ operation, category: CATEGORY.INVALID_RESPONSE, code: CODE.INVALID_RESPONSE, status, requestId, cause });
    }
}

const get = (url, params, headers, operation) => request(url, params, headers, 'GET', operation);
const post = (url, params, headers, operation) => request(url, params, headers, 'POST', operation);
const put = (url, params, headers, operation) => request(url, params, headers, 'PUT', operation);
const remove = (url, params, headers, operation) => request(url, params, headers, 'DELETE', operation);

export default { get, post, put, delete: remove };
